import { SkyText } from "@skypointcloud/skypoint-ui";
import { ExternalLinkIcon } from "@/lib/icons";
import { DBT_REPOSITORY_WITHHELD } from "@/lib/transformations/dbtSettings";
import { repoHostName, repoShortName, repoWebUrl } from "@/lib/transformations/repoFileUrl";

/**
 * #1306: a repository that is set but whose address is not this reader's to see (the graph's
 * `bundle.repoUrlWithheld`, below Instance admin): "Set." and who to ask, no address, no link.
 */
export function RepositoryWithheld({ size = 200, testId }: { size?: 200 | 300; testId?: string }) {
  return (
    <SkyText size={size} color="muted" testId={testId}>
      {DBT_REPOSITORY_WITHHELD}
    </SkyText>
  );
}

/**
 * #1349: an address saved as written (a host skyData has no page for) breaks after a slash (or a
 * dot) when the line is full, never in the middle of "sample-project.git". Only a token longer than
 * the line breaks inside. Shared by every place that shows such an address.
 */
export function BreakableAddress({
  repoUrl,
  size,
  testId,
  block = true,
}: {
  repoUrl: string;
  size: 200 | 300;
  testId?: string;
  block?: boolean;
}) {
  return (
    <SkyText
      size={size}
      font="monospace"
      block={block}
      className="min-w-0 [overflow-wrap:anywhere]"
      testId={testId}
    >
      {repoUrl.split(/(?<=[/.])/).map((part, index) => (
        <span key={index} className="inline-block max-w-full">
          {part}
        </span>
      ))}
    </SkyText>
  );
}

/**
 * Round 7 T-38: a bound dbt repository as a person reads it outside the facts strip: `owner/repo`
 * as a link to its page in a new tab (the external link icon), never the address as text. An
 * address on a host skyData does not know has no page to link, so it shows as it was saved.
 */
export function RepositoryName({ repoUrl, testId }: { repoUrl: string; testId?: string }) {
  const short = repoShortName(repoUrl);
  const href = repoWebUrl(repoUrl);
  const host = repoHostName(repoUrl);
  if (!short || !href) {
    return <BreakableAddress repoUrl={repoUrl} size={200} testId={testId} />;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={short}
      aria-label={`${short}, opens the repository on ${host ?? "its host"} in a new tab`}
      className="inline-flex min-w-0 max-w-full items-center gap-1 underline-offset-2 hover:underline"
      data-testid={testId}
    >
      <SkyText size={200} font="monospace" color="accent" className="min-w-0 break-all">
        {short}
      </SkyText>
      <ExternalLinkIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    </a>
  );
}
